import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { parseTokensFromUrl, storeTokensAndFetchUser, GoogleService } from '../../services/googleAuth';
import { useIntegrations } from '../../contexts/IntegrationsContext';

type CallbackStatus = 'loading' | 'success' | 'error';

// Global flag to prevent double execution in React Strict Mode (dev only)
let isProcessingCallback = false;

export default function AppOAuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshIntegrationStatus } = useIntegrations();
    const [status, setStatus] = useState<CallbackStatus>('loading');
    const [message, setMessage] = useState('Processing authentication...');
    const [connectedService, setConnectedService] = useState<GoogleService | null>(null);
    const processedRef = useRef(false);

    useEffect(() => {
        const handleCallback = async () => {
            // Prevent double execution in React Strict Mode
            if (isProcessingCallback) return;
            isProcessingCallback = true;

            if (processedRef.current) return;
            processedRef.current = true;

            // Implicit Flow: Check hash for access_token, or search for error
            // (Note: Google redirects with #access_token=... but React Router might put it in location.hash)

            // Check for OAuth error in query params (sometimes returned there)
            const error = searchParams.get('error');
            if (error) {
                setStatus('error');
                setMessage(error === 'access_denied'
                    ? 'You cancelled the authorization.'
                    : `Authorization failed: ${error}`
                );
                return;
            }

            // Verify state for CSRF protection (state is in access_token param or query?)
            // In implicit flow, state is returned in the hash fragment.
            // parseTokensFromUrl handles the hash parsing.

            // NOTE: We need to parse state from hash manually to verify BEFORE token use if strict
            // But parseTokensFromUrl extracts tokens. We can verify state from hash too.
            const hash = window.location.hash.substring(1);
            const params = new URLSearchParams(hash);
            const state = params.get('state');

            const storedState = localStorage.getItem('oauth_state');
            if (!state || state !== storedState) {
                // Try parsing query params for state (sometimes Google puts it there on error)
                const queryState = searchParams.get('state');
                if (!queryState || queryState !== storedState) {
                    console.error('State mismatch', { state, queryState, storedState });
                    setStatus('error');
                    setMessage('Invalid state parameter. Please try again.');
                    return;
                }
            }

            // Parse state to get service
            try {
                // Determine which state variable matched
                const validState = state || searchParams.get('state')!;
                const stateData = JSON.parse(validState);
                setConnectedService(stateData.service);
            } catch {
                console.warn('Could not parse state data');
            }

            // Parse tokens from URL
            const tokens = parseTokensFromUrl();
            if (!tokens) {
                setStatus('error');
                setMessage('No access token received. Please try again.');
                return;
            }

            setMessage('Verifying account...');
            const success = await storeTokensAndFetchUser(tokens);

            if (!success) {
                setStatus('error');
                setMessage('Failed to fetch account info. Please try again.');
                return;
            }

            // Clear stored state
            localStorage.removeItem('oauth_state');

            // Refresh integration status
            refreshIntegrationStatus();

            // Success!
            setStatus('success');
            setMessage('Successfully connected!');

            // Redirect immediately (short delay for visual confirmation)
            setTimeout(() => {
                navigate('/app/connections', { replace: true });
            }, 1000);
        };

        handleCallback();
    }, [searchParams, navigate, refreshIntegrationStatus]);

    return (
        <div className="min-h-screen bg-[#F5F8FA] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                {status === 'loading' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-[#D9EEF2] flex items-center justify-center mx-auto mb-4">
                            <Loader2 className="w-8 h-8 text-[#0D5F6B] animate-spin" />
                        </div>
                        <h1 className="text-xl font-semibold text-[#1A2832] mb-2">
                            Connecting...
                        </h1>
                        <p className="text-sm text-[#7B8C9B]">{message}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-[#D5F2EA] flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8 text-[#3DA58A]" />
                        </div>
                        <h1 className="text-xl font-semibold text-[#1A2832] mb-2">
                            {connectedService === 'gmail' ? 'Gmail' :
                                connectedService === 'calendar' ? 'Google Calendar' :
                                    'Google'} Connected!
                        </h1>
                        <p className="text-sm text-[#7B8C9B] mb-4">{message}</p>
                        <p className="text-xs text-[#7B8C9B]">Redirecting to connections...</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-[#FFEAE7] flex items-center justify-center mx-auto mb-4">
                            <XCircle className="w-8 h-8 text-[#D66B5C]" />
                        </div>
                        <h1 className="text-xl font-semibold text-[#1A2832] mb-2">
                            Connection Failed
                        </h1>
                        <p className="text-sm text-[#7B8C9B] mb-6">{message}</p>
                        <button
                            onClick={() => navigate('/app/connections', { replace: true })}
                            className="px-6 py-3 rounded-xl bg-[#0D5F6B] text-white font-semibold"
                        >
                            Back to Connections
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
